// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Calendar, AlertTriangle, TrendingUp, DollarSign, Star, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

type CoachSubscription = Database["public"]["Tables"]["coach_subscriptions"]["Row"];

const getRiskColor = (score: number) => {
  if (score >= 70) return "bg-red-500";
  if (score >= 40) return "bg-yellow-500";
  return "bg-green-500";
};

const getRiskLabel = (score: number) => {
  if (score >= 70) return "High Risk";
  if (score >= 40) return "Medium Risk";
  return "Low Risk";
};

export const RetentionManagement: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<CoachSubscription[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      const { data, error } = await supabase
        .from("coach_subscriptions")
        .select("*")
        .order("churn_risk_score", { ascending: false });

      if (error) throw error;
      setSubscriptions(data || []);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    } finally {
      setLoading(false);
    }
  };

  const getUpcomingRenewals = () => {
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);

    return subscriptions.filter(sub => {
      if (!sub.subscription_end_date) return false;
      const endDate = new Date(sub.subscription_end_date);
      return endDate <= threeMonthsFromNow && endDate > new Date();
    });
  };

  const getChurnRiskSubscriptions = () => {
    return subscriptions.filter(sub => 
      sub.churn_risk_score && sub.churn_risk_score >= 40
    );
  };

  const getUpsellOpportunities = () => {
    return subscriptions.filter(sub => 
      (sub.subscription_tier === "basic" || sub.subscription_tier === "trial") &&
      sub.feature_usage_score && sub.feature_usage_score >= 70
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-5 bg-gray-200 rounded w-32"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[...Array(3)].map((_, j) => (
                    <div key={j} className="h-16 bg-gray-100 rounded"></div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const upcomingRenewals = getUpcomingRenewals();
  const churnRiskSubs = getChurnRiskSubscriptions();
  const upsellOpportunities = getUpsellOpportunities();

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Calendar className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Upcoming Renewals</p>
                <p className="text-xl font-bold">{upcomingRenewals.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Churn Risk</p>
                <p className="text-xl font-bold">{churnRiskSubs.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Upsell Opportunities</p>
                <p className="text-xl font-bold">{upsellOpportunities.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Avg. LTV</p>
                <p className="text-xl font-bold">$2,400</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upcoming Renewals */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Upcoming Renewals
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {upcomingRenewals.slice(0, 5).map((sub) => (
              <div key={sub.id} className="p-3 border rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium text-sm">Coach #{sub.coach_id?.slice(0, 8)}</p>
                    <p className="text-xs text-gray-600">{sub.subscription_tier}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {sub.subscription_end_date}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">${sub.monthly_fee}/mo</span>
                  <Button size="sm" variant="outline" className="text-xs">
                    Renew
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Churn Risk */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Churn Risk Management
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {churnRiskSubs.slice(0, 5).map((sub) => (
              <div key={sub.id} className="p-3 border rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium text-sm">Coach #{sub.coach_id?.slice(0, 8)}</p>
                    <p className="text-xs text-gray-600">{sub.engagement_level}</p>
                  </div>
                  <Badge 
                    variant="outline" 
                    className={`text-xs ${getRiskColor(sub.churn_risk_score || 0)} text-white`}
                  >
                    {getRiskLabel(sub.churn_risk_score || 0)}
                  </Badge>
                </div>
                <div className="mb-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span>Risk Score</span>
                    <span>{sub.churn_risk_score}%</span>
                  </div>
                  <Progress value={sub.churn_risk_score || 0} className="h-2" />
                </div>
                <Button size="sm" variant="outline" className="text-xs w-full">
                  Intervention
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Upsell Opportunities */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5" />
              Upsell Opportunities
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {upsellOpportunities.slice(0, 5).map((sub) => (
              <div key={sub.id} className="p-3 border rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium text-sm">Coach #{sub.coach_id?.slice(0, 8)}</p>
                    <p className="text-xs text-gray-600">Current: {sub.subscription_tier}</p>
                  </div>
                  <Badge variant="outline" className="text-xs bg-green-50">
                    High Usage
                  </Badge>
                </div>
                <div className="mb-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span>Feature Usage</span>
                    <span>{sub.feature_usage_score}%</span>
                  </div>
                  <Progress value={sub.feature_usage_score || 0} className="h-2" />
                </div>
                <Button size="sm" variant="outline" className="text-xs w-full">
                  Upgrade Offer
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};